// @flow
import { renderToString } from "react-dom/server";
import { Provider } from "react-redux";
import { StaticRouter as Router } from "react-router";

import { HelmetProvider } from "react-helmet-async";

import { ServerStyleSheet, StyleSheetManager } from "styled-components";

import createSSRComplete from "./createSSRComplete";
import htmlpage from "./htmlpage";

import { getBasePathServer } from "../constants/Settings";

import ThemeProvider from "../react-theme/ThemeProvider";
import ErrorBoundary from "../react/ErrorBoundary";

import { createHead, handleErrors, dehydrate } from "./serverUtil";

import type { Theme } from "../react-theme/types";
import type { ReduxStore } from "../redux/types";
import type Href from "../models/href/Href";
import type { TemplateProps } from "./htmlpage";
import type { Location } from "react-router";

type Props = {
  store: ReduxStore,
  theme: Theme,
  UUID: string,
  requestHref: Href,
  render: Function,
  ErrorPage: any,
  template?: (TemplateProps) => string,
};
/**
 */
const renderSSRComplete = ({
  store,
  theme,
  UUID = "",
  requestHref,
  render,
  ErrorPage,
  template = htmlpage,
}: Props): Promise<string> => {
  const helmetContext = { helmet: {} };
  const routerContext = {};

  const sheet = new ServerStyleSheet();

  // $FlowFixMe[incompatible-type]
  // $FlowFixMe[prop-missing]
  const location: Location = requestHref.toLocation();

  const basePath = getBasePathServer();

  return createSSRComplete(store, () =>
    renderToString(
      <Provider store={store}>
        <StyleSheetManager sheet={sheet.instance}>
          <ThemeProvider theme={theme}>
            <HelmetProvider context={helmetContext}>
              <ErrorBoundary>
                <Router
                  basename={basePath}
                  location={location}
                  context={routerContext}
                >
                  {render()}
                </Router>
              </ErrorBoundary>
            </HelmetProvider>
          </ThemeProvider>
        </StyleSheetManager>
      </Provider>,
    ),
  )
    .then((appHTML) => {
      handleErrors(store);
      const head = createHead(sheet, UUID, helmetContext);
      return template({
        contextPath: basePath,
        html: appHTML,
        head,
        state: dehydrate(store),
        UUID,
      });
    })
    .catch((error) =>
      template({
        contextPath: basePath,
        html: renderToString(
          <StyleSheetManager sheet={sheet.instance}>
            <ThemeProvider theme={theme}>
              <ErrorPage
                errorMessage={error.message}
                errorResource={error.fileName}
                errorLine={error.lineNumber}
                errorStack={error.stack}
              />
            </ThemeProvider>
          </StyleSheetManager>,
        ),
        head: helmetContext.helmet,
        state: "",
        UUID,
      }),
    );
};

export default renderSSRComplete;
